/**
 * Campaign attribution for the lead gate.
 *
 * The UTM parameters that brought the visitor here are read once, on first
 * load, and kept in sessionStorage. The gate submits minutes later, by which
 * time the address bar may have been cleaned up, so the captured copy is what
 * the report request carries.
 *
 * `hutk` is HubSpot's own tracking cookie. It is only readable when the
 * HubSpot script is on the page; absent, the submission is simply anonymous.
 */

import type { LeadContext } from "@/lib/api";
import { logger } from "@/lib/logger";

import { EMBED_PAGE_NAME, getParentPageTitle, getParentPageUrl, isEmbedded } from "./embed";

export const ATTRIBUTION_KEY = "hw-attribution-v1";

/** The HubSpot tracking cookie, when their script has set one. */
export const HUBSPOT_COOKIE = "hubspotutk";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;

export type UtmKey = (typeof UTM_KEYS)[number];

/** The captured `utm_*` pairs. Absent keys were not in the landing URL. */
export type UtmParams = Partial<Record<UtmKey, string>>;

/** How much of a single parameter is kept, so a crafted URL cannot bloat storage. */
const MAX_VALUE_CHARS = 200;

/** Read the `utm_*` pairs out of a query string, dropping empty values. */
export function parseUtm(search: string): UtmParams {
  const params = new URLSearchParams(search);
  const captured: UtmParams = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key)?.trim();
    if (value) {
      captured[key] = value.slice(0, MAX_VALUE_CHARS);
    }
  }
  return captured;
}

/** Read one cookie out of a `document.cookie` string. */
export function readCookie(name: string, cookie: string): string | undefined {
  for (const part of cookie.split(";")) {
    const separator = part.indexOf("=");
    if (separator === -1) {
      continue;
    }
    if (part.slice(0, separator).trim() !== name) {
      continue;
    }
    const value = part.slice(separator + 1).trim();
    return value ? decodeURIComponent(value) : undefined;
  }
  return undefined;
}

/** Narrow a parsed storage payload back to `UtmParams`, ignoring anything else. */
function validate(parsed: unknown): UtmParams {
  if (typeof parsed !== "object" || parsed === null) {
    return {};
  }
  const candidate = parsed as Record<string, unknown>;
  const stored: UtmParams = {};
  for (const key of UTM_KEYS) {
    const value = candidate[key];
    if (typeof value === "string" && value) {
      stored[key] = value.slice(0, MAX_VALUE_CHARS);
    }
  }
  return stored;
}

function readStored(): UtmParams | null {
  try {
    const raw = window.sessionStorage.getItem(ATTRIBUTION_KEY);
    return raw ? validate(JSON.parse(raw)) : null;
  } catch (error) {
    logger.warn("[diagnostic]", "could not read the saved attribution", error);
    return null;
  }
}

/**
 * Capture the landing URL's `utm_*` pairs, once.
 *
 * A URL that carries parameters wins: it is the campaign that just brought the
 * visitor in. A URL without any leaves whatever was captured earlier in place,
 * so a client-side navigation cannot erase the campaign.
 */
export function captureAttribution(): UtmParams {
  if (typeof window === "undefined") {
    return {};
  }
  const fromUrl = parseUtm(window.location.search);
  const stored = readStored();
  if (Object.keys(fromUrl).length === 0) {
    return stored ?? {};
  }
  try {
    window.sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(fromUrl));
    logger.debug("[diagnostic]", "captured attribution", Object.keys(fromUrl));
  } catch (error) {
    logger.warn("[diagnostic]", "could not save the attribution", error);
  }
  return fromUrl;
}

/**
 * The submission context the report request carries: the captured campaign,
 * the page it was submitted from, and HubSpot's visitor cookie when present.
 *
 * Embedded, the page the visitor is on is the parent page, not the iframe, so
 * that is what the lead record gets. A cross-origin parent's title is
 * unreadable, so the page is named rather than left blank.
 */
export function getLeadContext(): LeadContext {
  if (typeof window === "undefined") {
    return {};
  }
  const utm = captureAttribution();
  const hutk =
    typeof document === "undefined" ? undefined : readCookie(HUBSPOT_COOKIE, document.cookie);
  const embedded = isEmbedded();
  const parentUrl = embedded ? getParentPageUrl() : null;
  const ownTitle = typeof document === "undefined" ? undefined : document.title;

  return {
    ...(hutk ? { hutk } : {}),
    page_uri: parentUrl ?? window.location.href,
    page_name: embedded ? (getParentPageTitle() ?? EMBED_PAGE_NAME) : ownTitle,
    ...utm,
  };
}
